// The Connect event (CC §4.5): the complete page posts `{service, result}` on one
// BroadcastChannel, which every tab of the CRM's origin hears. A provider may cut
// `window.opener` (COOP), so the opener is never relied on.

export const CHANNEL_NAME = 'crm-integrations';

/** A Connect's end, as the complete page read it: `service` lowercase or empty, `result` a code. */
export interface ConnectResultMessage {
  service: string;
  result: string;
}

const SERVICE_SHAPE = /^[a-z][a-z0-9_]*$/;
const RESULT_SHAPE = /^[a-z_]+$/;

/**
 * The shape checks of CC D16: a service the backend adds later, or a result code, passes with no
 * change; anything else is never posted or heard.
 */
export function isResultMessage(value: unknown): value is ConnectResultMessage {
  if (!value || typeof value !== 'object') return false;
  const { service, result } = value as Record<string, unknown>;
  return (
    typeof service === 'string' &&
    (service === '' || SERVICE_SHAPE.test(service)) &&
    typeof result === 'string' &&
    RESULT_SHAPE.test(result)
  );
}

/** Posts a Connect's end when its values pass the shape checks; true when it was posted. */
export function postResult(message: { service: string; result: string }): boolean {
  const posted = { service: message.service, result: message.result };
  if (!isResultMessage(posted) || typeof BroadcastChannel === 'undefined') return false;
  const channel = new BroadcastChannel(CHANNEL_NAME);
  channel.postMessage(posted);
  channel.close();
  return true;
}

/** Hears every Connect's end posted on the channel; returns the unsubscribe. */
export function onResult(listener: (message: ConnectResultMessage) => void): () => void {
  if (typeof BroadcastChannel === 'undefined') return () => undefined;
  const channel = new BroadcastChannel(CHANNEL_NAME);
  channel.onmessage = (event: MessageEvent) => {
    const data: unknown = event.data;
    if (isResultMessage(data)) listener({ service: data.service, result: data.result });
  };
  return () => {
    channel.close();
  };
}
